import { styled } from '@mui/material/styles';
import AssistantCitation from 'ai-assistant/citations/assistant-citation';
import React from 'react';

/** Long enough for a clause, short enough for the two lines the preview shows. */
const PASSAGE_LIMIT = 160;

/**
 * The first highlighted passage of a citation's excerpt, as plain text on one line: the words the
 * claim rests on. Nothing when the excerpt highlights nothing.
 *
 * The excerpt is escaped source text with `<mark>` around the evidence, as the server rendered it.
 * It is parsed rather than matched, so an entity such as `&amp;` reads as the character it is.
 * DOMParser runs no script and loads nothing, and only the text is kept.
 */
export function firstHighlight(html: string | undefined): string | undefined {
  if (!html) return undefined;
  const mark = new DOMParser().parseFromString(html, 'text/html').querySelector('mark');
  const passage = mark?.textContent?.replace(/\s+/g, ' ').trim();
  if (!passage) return undefined;
  return passage.length > PASSAGE_LIMIT ? `${passage.slice(0, PASSAGE_LIMIT - 1).trimEnd()}…` : passage;
}

const Source = styled('span')(({ theme }) => ({
  display: 'block',
  fontWeight: theme.typography.fontWeightMedium,
  overflowWrap: 'anywhere',
}));

const Passage = styled('span')(({ theme }) => ({
  display: '-webkit-box',
  marginTop: theme.spacing(0.5),
  WebkitBoxOrient: 'vertical',
  WebkitLineClamp: 2,
  overflow: 'hidden',
  fontStyle: 'italic',
}));

/**
 * What a marker in an answer points at, shown on hover or focus so the reader can check a source
 * without leaving the sentence (#5987): the source's name, its page, and the passage the claim
 * rests on. A withheld citation shows its name only, because the reader may no longer see the text.
 */
export default function CitationPreview({ citation }: Readonly<{ citation: AssistantCitation }>) {
  const passage = citation.withheld ? undefined : firstHighlight(citation.text);
  return (
    <>
      <Source>
        {citation.sourceLabel}
        {citation.pageName ? ` (Page ${citation.pageName})` : null}
      </Source>
      {passage ? <Passage data-testid="citation-preview-passage">{`“${passage}”`}</Passage> : null}
    </>
  );
}
